import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Grid,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  ViewWeek as ViewWeekIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { ro } from 'date-fns/locale';

export interface CalendarAppointment {
  id: string;
  date: string;
  time: string;
  patientName: string;
  patientSpecies: string;
  patientBreed?: string;
  patientAge?: number;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  doctorName: string;
  doctorId?: string;
  service: string;
  status: 'pending_confirmation' | 'confirmed' | 'scheduled' | 'checked_in' | 'completed' | 'no_show' | 'cancelled';
  location: string;
  notes?: string;
  isUrgent?: boolean;
  source?: 'online' | 'phone' | 'facebook' | 'walk_in';
  visitType?: 'clinic' | 'home' | 'online';
  duration: number;
  price?: number;
  assignedRoom?: string;
  assignedEquipment?: string[];
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
  confirmedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  reminders?: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
  color?: string;
}

interface AppointmentsCalendarProps {
  appointments: CalendarAppointment[];
  onAppointmentClick: (appointment: CalendarAppointment) => void;
  onAppointmentDrag: (appointmentId: string, newDate: string, newTime: string) => void;
  onDateClick: (date: Date) => void;
}

type CalendarView = 'month' | 'week' | 'day';

const AppointmentsCalendar: React.FC<AppointmentsCalendarProps> = ({
  appointments,
  onAppointmentClick,
  onAppointmentDrag,
  onDateClick
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [draggedAppointment, setDraggedAppointment] = useState<string | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);

  // Calendar navigation
  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.date === dateStr);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return '#4CAF50';
      case 'pending_confirmation':
        return '#FF9800';
      case 'scheduled':
        return '#2196F3';
      case 'completed':
        return '#8BC34A';
      case 'cancelled':
        return '#F44336';
      case 'no_show':
        return '#9E9E9E';
      case 'checked_in':
        return '#00BCD4';
      default:
        return '#757575';
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, appointmentId: string) => {
    setDraggedAppointment(appointmentId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    setDragOverDate(dateStr);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    if (draggedAppointment) {
      // For simplicity, we'll just move to the same time on the new date
      // In a real implementation, you'd want to handle time slots
      onAppointmentDrag(draggedAppointment, dateStr, '10:00');
    }
    setDraggedAppointment(null);
    setDragOverDate(null);
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start week on Monday
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const dateFormat = 'd';
    const rows = [];
    let days = [];
    let day = startDate;

    // Week headers
    const weekDays = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sam', 'Dum'];
    const weekHeaders = (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: 1, borderColor: 'divider' }}>
        {weekDays.map((dayName, index) => (
          <Box key={index} sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {dayName}
            </Typography>
          </Box>
        ))}
      </Box>
    );

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayAppointments = getAppointmentsForDate(day);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isToday = isSameDay(day, new Date());
        const dayStr = format(day, 'yyyy-MM-dd');

        days.push(
          <Box
            key={dayStr}
            sx={{
              minHeight: 120,
              p: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: isCurrentMonth ? 'background.paper' : 'grey.50',
              opacity: isCurrentMonth ? 1 : 0.5,
              position: 'relative',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'primary.light',
                opacity: 0.8
              },
              ...(isToday && {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }),
              ...(dragOverDate === dayStr && {
                bgcolor: 'secondary.light',
                opacity: 0.7
              })
            }}
            onDragOver={(e) => handleDragOver(e, dayStr)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, dayStr)}
            onClick={() => onDateClick(day)}
          >
            <Box sx={{ p: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight={isToday ? 700 : 500}
                sx={{ mb: 0.5 }}
              >
                {format(day, dateFormat)}
              </Typography>
              
              {/* Appointments for this day */}
              <Box sx={{ maxHeight: 80, overflow: 'hidden' }}>
                {dayAppointments.slice(0, 3).map((appointment) => (
                  <Card
                    key={appointment.id}
                    sx={{
                      mb: 0.5,
                      p: 0.5,
                      cursor: 'grab',
                      bgcolor: getStatusColor(appointment.status),
                      color: 'white',
                      '&:active': {
                        cursor: 'grabbing'
                      },
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.02)'
                      }
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, appointment.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick(appointment);
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {appointment.time} - {appointment.patientName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {appointment.clientName}
                    </Typography>
                  </Card>
                ))}
                {dayAppointments.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{dayAppointments.length - 3} mai multe
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <Box key={day.toString()} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days}
        </Box>
      );
      days = [];
    }

    return (
      <Box>
        {weekHeaders}
        {rows}
      </Box>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    
    const days = [];
    let day = weekStart;
    
    const weekDays = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata', 'Duminica'];
    
    for (let i = 0; i < 7; i++) {
      const dayAppointments = getAppointmentsForDate(day);
      const isToday = isSameDay(day, new Date());
      const dayStr = format(day, 'yyyy-MM-dd');
      
      days.push(
        <Box key={dayStr} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 2, minHeight: 400 }}>
            <Typography
              variant="h6"
              fontWeight={isToday ? 700 : 500}
              sx={{
                mb: 2,
                textAlign: 'center',
                bgcolor: isToday ? 'primary.main' : 'transparent',
                color: isToday ? 'primary.contrastText' : 'text.primary',
                borderRadius: 1,
                p: 1
              }}
            >
              {weekDays[i]}
              <br />
              {format(day, 'd MMM', { locale: ro })}
            </Typography>
            
            <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
              {dayAppointments.map((appointment) => (
                <Card
                  key={appointment.id}
                  sx={{
                    mb: 1,
                    p: 1,
                    cursor: 'grab',
                    bgcolor: getStatusColor(appointment.status),
                    color: 'white',
                    '&:active': {
                      cursor: 'grabbing'
                    },
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.02)'
                    }
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, appointment.id)}
                  onClick={() => onAppointmentClick(appointment)}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {appointment.time} - {appointment.patientName}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {appointment.clientName} • {appointment.service}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      );
      day = addDays(day, 1);
    }

    return <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>{days}</Box>;
  };

  // Render day view
  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDate(currentDate);
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {format(currentDate, 'EEEE, d MMMM yyyy', { locale: ro })}
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {dayAppointments.map((appointment) => (
            <Box key={appointment.id}>
              <Card
                sx={{
                  cursor: 'grab',
                  borderLeft: `4px solid ${getStatusColor(appointment.status)}`,
                  '&:active': {
                    cursor: 'grabbing'
                  },
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, appointment.id)}
                onClick={() => onAppointmentClick(appointment)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: getStatusColor(appointment.status) }}>
                      {appointment.patientSpecies === 'Caine' ? '🐶' : 
                       appointment.patientSpecies === 'Pisica' ? '🐱' : '🐾'}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {appointment.time} - {appointment.patientName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {appointment.patientSpecies} • {appointment.patientAge} ani
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Client:</strong> {appointment.clientName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Doctor:</strong> {appointment.doctorName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Serviciu:</strong> {appointment.service}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Durata:</strong> {appointment.duration} min
                  </Typography>
                  
                  {appointment.isUrgent && (
                    <Chip label="URGENT" color="error" size="small" sx={{ mt: 1 }} />
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
        
        {dayAppointments.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Nu ai programari pentru aceasta zi
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Calendar Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            {format(currentDate, 'MMMM yyyy', { locale: ro })}
          </Typography>
          <IconButton onClick={handlePrevMonth}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={handleNextMonth}>
            <ChevronRightIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={handleToday}
            size="small"
          >
            Astazi
          </Button>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Button
            variant={view === 'month' ? 'contained' : 'outlined'}
            startIcon={<ViewModuleIcon />}
            onClick={() => setView('month')}
            size="small"
          >
            Luna
          </Button>
          <Button
            variant={view === 'week' ? 'contained' : 'outlined'}
            startIcon={<ViewWeekIcon />}
            onClick={() => setView('week')}
            size="small"
          >
            Saptamana
          </Button>
          <Button
            variant={view === 'day' ? 'contained' : 'outlined'}
            startIcon={<ViewListIcon />}
            onClick={() => setView('day')}
            size="small"
          >
            Zi
          </Button>
        </Stack>
      </Box>

      {/* Calendar Content */}
      <Box sx={{ minHeight: 500 }}>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Legenda statusuri:
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#4CAF50', borderRadius: 0.5 }} />
            <Typography variant="caption">Confirmata</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#FF9800', borderRadius: 0.5 }} />
            <Typography variant="caption">In asteptare</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#2196F3', borderRadius: 0.5 }} />
            <Typography variant="caption">Programata</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#8BC34A', borderRadius: 0.5 }} />
            <Typography variant="caption">Finalizata</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#F44336', borderRadius: 0.5 }} />
            <Typography variant="caption">Anulata</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#9E9E9E', borderRadius: 0.5 }} />
            <Typography variant="caption">No-show</Typography>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default AppointmentsCalendar;
